// =============================================================================
// src/features/auth/login-takeover.ts — Comportamento da tela de login (gate).
//
// 2026-07-20 (auth REAL): o formulário valida e-mail/senha no servidor local
//   (server/auth-server.ts via auth-api-client) — só usuários CADASTRADOS
//   (npm run auth:add-user) entram. "Esqueceu a senha?" abre a visão de
//   redefinição: pede o código de 6 dígitos por e-mail (SMTP, remetente
//   no-reply@r2tgroup.com) e confirma código + nova senha. Os botões sociais
//   (Google/gov.br) seguem SEM autenticar (decisão do usuário 2026-07-20) —
//   mostram nota inline. A sessão persistida continua em auth-session.ts e o
//   chamador decide o pós-login (main.ts → bootstrap; pós-logout → nada).
//
// DOM criado LAZY no present (padrão de chrome/settings-panel); a tela NÃO
//   fecha por Esc/backdrop — sem sessão não há app por baixo para voltar.
//   Notas são INLINE no card (MiniToast só existe após o bootstrap).
// =============================================================================

import { persistAuthSession, clearAuthSession } from './auth-session';
import type { AuthSessionStorage } from './auth-session';
import { buildLoginTakeoverMarkup, buildLoginSubtitleText, AUTH_LOGIN_IDS } from './login-markup';
import { createAuthApiClient } from './auth-api-client';
import type { AuthApiClient, AuthApiCallResult } from './auth-api-client';
import { resolveAuthApiBaseUrl } from './auth-api-config';
import { qs } from '../../app/dom';

/** Id do host fixo da tela (greppável; no máximo um por documento). */
export const AUTH_LOGIN_HOST_ID = 'authLoginTakeover';

/** Body class enquanto a tela está aberta (trava o scroll do shell por baixo). */
export const AUTH_LOCKED_BODY_CLASS = 'is-auth-locked';

/** Validação de boundary do formulário de login. */
const AUTH_EMPTY_FIELDS_MESSAGE = 'Informe e-mail e senha para entrar.';

/** Servidor de auth fora do ar / rede caiu (auth-api-client -> 'indisponivel'). */
const AUTH_API_UNREACHABLE_MESSAGE =
  'Não foi possível falar com o servidor de autenticação. Verifique a conexão e tente novamente.';

/** Mensagens do fluxo de redefinição de senha. */
const AUTH_RESET_EMAIL_REQUIRED_MESSAGE = 'Informe o e-mail cadastrado para receber o código.';
const AUTH_RESET_CODE_SENT_MESSAGE =
  'Se o e-mail estiver cadastrado, o código chegou na caixa de entrada — vale 30 minutos.';
const AUTH_RESET_FIELDS_REQUIRED_MESSAGE = 'Informe o código recebido por e-mail e a nova senha.';
const AUTH_RESET_DONE_MESSAGE = 'Senha redefinida — entre com a nova senha.';

/** Títulos por visão do card. */
const AUTH_LOGIN_TITLE = 'Bem-vindo de volta';
const AUTH_RESET_TITLE = 'Redefinir senha';
const AUTH_RESET_SUBTITLE = 'Enviaremos um código de 6 dígitos para o seu e-mail cadastrado.';

/** Notas dos gatilhos que não autenticam (sociais + pedido de acesso). */
const AUTH_REQUEST_ACCESS_HINT = 'Novos acessos são concedidos pelo administrador do gabinete.';

/** Nota dos botões sociais (2026-07-20: social NÃO loga direto — só o formulário). */
function socialSignInUnavailableHint(providerLabel: string): string {
  return `Entrar com ${providerLabel} ainda não está habilitado para este gabinete — use seu e-mail institucional.`;
}

/** Tom visual da nota inline: erro (danger) ou orientação/confirmação (info). */
type LoginNoteTone = 'danger' | 'info';

/** Opções do gate: callback pós-login + storage e cliente de API injetáveis. */
export interface LoginTakeoverOptions {
  readonly onAuthenticated?: () => void;
  readonly storage?: AuthSessionStorage;
  readonly apiClient?: AuthApiClient;
}

/**
 * Apresenta a tela de login (cria o host no body; idempotente se já aberta).
 *
 * @example presentLoginTakeover({ onAuthenticated: () => void bootstrap() });
 */
export function presentLoginTakeover(options: LoginTakeoverOptions = {}): void {
  if (document.getElementById(AUTH_LOGIN_HOST_ID)) return;
  const host = document.createElement('div');
  host.id = AUTH_LOGIN_HOST_ID;
  host.className = 'auth-login-takeover';
  host.innerHTML = buildLoginTakeoverMarkup();
  document.body.appendChild(host);
  document.body.classList.add(AUTH_LOCKED_BODY_CLASS);
  bindLoginTakeover(host, options);
  focusLoginInput(host, AUTH_LOGIN_IDS.email);
}

/** Remove a tela e destrava o scroll do body (no-op se a tela não está aberta). */
export function dismissLoginTakeover(): void {
  document.getElementById(AUTH_LOGIN_HOST_ID)?.remove();
  document.body.classList.remove(AUTH_LOCKED_BODY_CLASS);
}

/**
 * Encerra a sessão (ação "Sair" da topbar) e reapresenta a tela de login por
 * cima do app já montado — sem reload nem re-bootstrap.
 *
 * @example signOutOperator(); // topbar account menu, data-action="signout"
 */
export function signOutOperator(options: LoginTakeoverOptions = {}): void {
  clearAuthSession(options.storage);
  presentLoginTakeover(options);
}

/** Cliente de API efetivo: o injetado (testes) ou o real (servidor local). */
function resolveLoginApiClient(options: LoginTakeoverOptions): AuthApiClient {
  return options.apiClient ?? createAuthApiClient({ baseUrl: resolveAuthApiBaseUrl(import.meta.env) });
}

/** Liga formulário, redefinição, sociais e links de orientação do card. */
function bindLoginTakeover(host: HTMLElement, options: LoginTakeoverOptions): void {
  const apiClient = resolveLoginApiClient(options);
  bindLoginForm(host, options, apiClient);
  bindResetNavigation(host);
  bindResetRequest(host, apiClient);
  bindResetConfirm(host, apiClient);
  bindLoginHintTrigger(host, AUTH_LOGIN_IDS.google, socialSignInUnavailableHint('Google'));
  bindLoginHintTrigger(host, AUTH_LOGIN_IDS.govbr, socialSignInUnavailableHint('gov.br'));
  bindLoginHintTrigger(host, AUTH_LOGIN_IDS.requestAccess, AUTH_REQUEST_ACCESS_HINT);
}

/** Submit do formulário: preventDefault + validação + verificação no servidor. */
function bindLoginForm(host: HTMLElement, options: LoginTakeoverOptions, apiClient: AuthApiClient): void {
  const form = qs<HTMLFormElement>(`#${AUTH_LOGIN_IDS.form}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!form) return;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    void submitLoginForm(host, options, apiClient);
  });
}

/** Valida o par não vazio e checa as credenciais no servidor (auth real). */
async function submitLoginForm(
  host: HTMLElement,
  options: LoginTakeoverOptions,
  apiClient: AuthApiClient,
): Promise<void> {
  const operatorEmail = loginInputValue(host, AUTH_LOGIN_IDS.email);
  const senha = loginInputValue(host, AUTH_LOGIN_IDS.password);
  if (!operatorEmail || !senha) {
    showLoginNote(host, AUTH_EMPTY_FIELDS_MESSAGE, 'danger');
    focusLoginInput(host, operatorEmail ? AUTH_LOGIN_IDS.password : AUTH_LOGIN_IDS.email);
    return;
  }
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.submit, true);
  const result = await apiClient.verifyLogin(operatorEmail, senha);
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.submit, false);
  applyLoginVerdict(host, options, operatorEmail, result);
}

/** Mapeia o resultado do servidor: entra, recusa com mensagem, ou indisponível. */
function applyLoginVerdict(
  host: HTMLElement,
  options: LoginTakeoverOptions,
  operatorEmail: string,
  result: AuthApiCallResult,
): void {
  if (result.kind === 'ok') {
    persistAuthSession({ operatorEmail, provider: 'email' }, options.storage);
    dismissLoginTakeover();
    options.onAuthenticated?.();
    return;
  }
  const message = result.kind === 'recusado' ? result.message : AUTH_API_UNREACHABLE_MESSAGE;
  showLoginNote(host, message, 'danger');
}

/** "Esqueceu a senha?" abre a visão de redefinição; "Voltar ao login" fecha. */
function bindResetNavigation(host: HTMLElement): void {
  bindLoginClick(host, AUTH_LOGIN_IDS.forgot, () => showResetView(host));
  bindLoginClick(host, AUTH_LOGIN_IDS.resetBack, () => showLoginView(host));
}

/** Troca para a visão de redefinição (título/subtítulo próprios + e-mail prefilled). */
function showResetView(host: HTMLElement): void {
  toggleLoginViews(host, 'reset');
  setLoginHeading(host, AUTH_RESET_TITLE, AUTH_RESET_SUBTITLE);
  setLoginInputValue(host, AUTH_LOGIN_IDS.resetEmail, loginInputValue(host, AUTH_LOGIN_IDS.email));
  hideLoginNote(host);
  focusLoginInput(host, AUTH_LOGIN_IDS.resetEmail);
}

/** Volta para a visão de login (restaura título/subtítulo do gabinete). */
function showLoginView(host: HTMLElement): void {
  toggleLoginViews(host, 'login');
  setLoginHeading(host, AUTH_LOGIN_TITLE, buildLoginSubtitleText());
  focusLoginInput(host, AUTH_LOGIN_IDS.email);
}

/** Alterna [hidden] entre as duas visões do card. */
function toggleLoginViews(host: HTMLElement, active: 'login' | 'reset'): void {
  const loginView = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.loginView}`, host);
  const resetView = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.resetView}`, host);
  /* v8 ignore next -- guarda defensiva: os ids vêm do MESMO login-markup que gerou o host */
  if (!loginView || !resetView) return;
  loginView.hidden = active !== 'login';
  resetView.hidden = active !== 'reset';
}

/** Atualiza título + subtítulo do card (cada visão tem os seus). */
function setLoginHeading(host: HTMLElement, title: string, subtitle: string): void {
  const titleEl = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.title}`, host);
  const subtitleEl = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.subtitle}`, host);
  /* v8 ignore next -- guarda defensiva: os ids vêm do MESMO login-markup que gerou o host */
  if (!titleEl || !subtitleEl) return;
  titleEl.textContent = title;
  subtitleEl.textContent = subtitle;
}

/** "Enviar código": pede ao servidor o e-mail com o código de redefinição. */
function bindResetRequest(host: HTMLElement, apiClient: AuthApiClient): void {
  bindLoginClick(host, AUTH_LOGIN_IDS.resetRequest, () => void submitResetRequest(host, apiClient));
}

/** Valida o e-mail e dispara o envio; sucesso revela o passo 2 (código+senha). */
async function submitResetRequest(host: HTMLElement, apiClient: AuthApiClient): Promise<void> {
  const email = loginInputValue(host, AUTH_LOGIN_IDS.resetEmail);
  if (!email) {
    showLoginNote(host, AUTH_RESET_EMAIL_REQUIRED_MESSAGE, 'danger');
    focusLoginInput(host, AUTH_LOGIN_IDS.resetEmail);
    return;
  }
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.resetRequest, true);
  const result = await apiClient.requestResetCode(email);
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.resetRequest, false);
  applyResetRequestVerdict(host, result);
}

/** Sucesso: nota info + passo 2 visível e focado. Falha: nota danger. */
function applyResetRequestVerdict(host: HTMLElement, result: AuthApiCallResult): void {
  if (result.kind === 'ok') {
    showLoginNote(host, AUTH_RESET_CODE_SENT_MESSAGE, 'info');
    revealResetStep2(host);
    return;
  }
  const message = result.kind === 'recusado' ? result.message : AUTH_API_UNREACHABLE_MESSAGE;
  showLoginNote(host, message, 'danger');
}

/** Revela os campos código + nova senha e foca o código. */
function revealResetStep2(host: HTMLElement): void {
  const step2 = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.resetStep2}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!step2) return;
  step2.hidden = false;
  focusLoginInput(host, AUTH_LOGIN_IDS.resetCode);
}

/** "Redefinir senha": confirma código + nova senha no servidor. */
function bindResetConfirm(host: HTMLElement, apiClient: AuthApiClient): void {
  bindLoginClick(host, AUTH_LOGIN_IDS.resetConfirm, () => void submitResetConfirm(host, apiClient));
}

/** Valida os campos e confirma; sucesso volta ao login com nota de confirmação. */
async function submitResetConfirm(host: HTMLElement, apiClient: AuthApiClient): Promise<void> {
  const email = loginInputValue(host, AUTH_LOGIN_IDS.resetEmail);
  const codigo = loginInputValue(host, AUTH_LOGIN_IDS.resetCode);
  const novaSenha = loginInputValue(host, AUTH_LOGIN_IDS.resetNewPassword);
  if (!codigo || !novaSenha) {
    showLoginNote(host, AUTH_RESET_FIELDS_REQUIRED_MESSAGE, 'danger');
    focusLoginInput(host, codigo ? AUTH_LOGIN_IDS.resetNewPassword : AUTH_LOGIN_IDS.resetCode);
    return;
  }
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.resetConfirm, true);
  const result = await apiClient.confirmReset(email, codigo, novaSenha);
  setLoginButtonBusy(host, AUTH_LOGIN_IDS.resetConfirm, false);
  applyResetConfirmVerdict(host, email, result);
}

/** Sucesso: volta ao login com o e-mail preenchido e nota info. Falha: danger. */
function applyResetConfirmVerdict(host: HTMLElement, email: string, result: AuthApiCallResult): void {
  if (result.kind === 'ok') {
    showLoginView(host);
    setLoginInputValue(host, AUTH_LOGIN_IDS.email, email);
    setLoginInputValue(host, AUTH_LOGIN_IDS.password, '');
    showLoginNote(host, AUTH_RESET_DONE_MESSAGE, 'info');
    return;
  }
  const message = result.kind === 'recusado' ? result.message : AUTH_API_UNREACHABLE_MESSAGE;
  showLoginNote(host, message, 'danger');
}

/** Gatilho de orientação (botão social OU link): preventDefault + nota info no card. */
function bindLoginHintTrigger(host: HTMLElement, triggerId: string, hint: string): void {
  bindLoginClick(host, triggerId, () => showLoginNote(host, hint, 'info'));
}

/** Liga um click com preventDefault num elemento do card (links e botões). */
function bindLoginClick(host: HTMLElement, elementId: string, onClick: () => void): void {
  const element = qs<HTMLElement>(`#${elementId}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!element) return;
  element.addEventListener('click', (event) => {
    event.preventDefault();
    onClick();
  });
}

/** Valor trim() de um input do card por id ('' se vazio). */
function loginInputValue(host: HTMLElement, inputId: string): string {
  const input = qs<HTMLInputElement>(`#${inputId}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!input) return '';
  return input.value.trim();
}

/** Define o valor de um input do card por id. */
function setLoginInputValue(host: HTMLElement, inputId: string, value: string): void {
  const input = qs<HTMLInputElement>(`#${inputId}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!input) return;
  input.value = value;
}

/** Exibe a nota de status do card com o tom pedido (danger|info). */
function showLoginNote(host: HTMLElement, message: string, tone: LoginNoteTone): void {
  const note = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.note}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!note) return;
  note.textContent = message;
  note.classList.toggle('is-danger', tone === 'danger');
  note.classList.toggle('is-info', tone === 'info');
  note.hidden = false;
}

/** Esconde a nota (troca de visão limpa o status anterior). */
function hideLoginNote(host: HTMLElement): void {
  const note = qs<HTMLElement>(`#${AUTH_LOGIN_IDS.note}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!note) return;
  note.hidden = true;
}

/** Liga/desliga o estado ocupado de um botão (evita duplo submit durante o await). */
function setLoginButtonBusy(host: HTMLElement, buttonId: string, busy: boolean): void {
  const button = qs<HTMLButtonElement>(`#${buttonId}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!button) return;
  button.disabled = busy;
}

/** Foca um input do card por id (a11y: e-mail no present; campo ofensor no erro). */
function focusLoginInput(host: HTMLElement, inputId: string): void {
  const input = qs<HTMLInputElement>(`#${inputId}`, host);
  /* v8 ignore next -- guarda defensiva: o id vem do MESMO login-markup que gerou o host */
  if (!input) return;
  input.focus();
}
